// Samostatné okno mapy provozu (GPS etapa 3 část G, krok 11.12; zadání § 2 bod 11): odkazy z okna (karta zákazníka,
// nová zakázka, karta zakázky) se otevírají v hlavním okně aplikace — okno pošle `window.opener.postMessage` s cestou,
// hlavní okno ji převezme a naviguje (`useNavigaceZOkna` v `App.tsx`); okno bez openeru (otevřené z adresy, hlavní
// okno zavřené) otevře novou kartu. Zprávy jen ze stejného původu a jen relativní cesty v aplikaci.
//
// Soubor je schválně malý a bez závislostí na kódu sekce: `App.tsx` ho importuje staticky (posluchač musí běžet
// na každé obrazovce hlavního okna), takže je v hlavním balíku — mapa ani sekce se tím do něj nedostanou (docs/09 §12).
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

/** Typ zprávy mezi oknem mapy a hlavním oknem. */
export const TYP_ZPRAVY_NAVIGACE = 'ecoser-mapa-navigace';
/** Cesta a jméno samostatného okna (znovu otevřené tlačítko okno jen přenese dopředu). */
export const CESTA_OKNA_MAPY = '/mapa-provozu/okno';
export const JMENO_OKNA_MAPY = 'ecoser-mapa';
/** Samostatné okno mapy zákazníků (rozhodnutí zadavatele 6. 10. 2026 k 05/2173 b) — filtry seznamu v dotazu adresy. */
export const CESTA_OKNA_MAPY_ZAKAZNIKU = '/zakaznici/mapa-okno';
export const JMENO_OKNA_MAPY_ZAKAZNIKU = 'ecoser-mapa-zakazniku';

/** Cesta ze zprávy okna — jen `{ typ, cesta }` s relativní cestou v aplikaci (`/…`, ne `//…`); jinak null. */
export function cestaZeZpravy(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const zprava = data as { typ?: unknown; cesta?: unknown };
  if (zprava.typ !== TYP_ZPRAVY_NAVIGACE || typeof zprava.cesta !== 'string') return null;
  const cesta = zprava.cesta;
  if (!cesta.startsWith('/') || cesta.startsWith('//') || cesta.includes('\\')) return null;
  return cesta;
}

/**
 * Otevře cestu v hlavním okně (volá okno mapy): opener stejného původu dostane zprávu a naviguje; bez openeru (nebo
 * když zpráva nejde poslat) nová karta.
 */
export function otevritVHlavnimOkne(cesta: string): void {
  const hlavni = window.opener as Window | null;
  if (hlavni && !hlavni.closed) {
    try {
      hlavni.postMessage({ typ: TYP_ZPRAVY_NAVIGACE, cesta }, window.location.origin);
      hlavni.focus();
      return;
    } catch {
      // Opener jiného původu nebo zavřený mezi kontrolou a odesláním — nová karta.
    }
  }
  window.open(cesta, '_blank', 'noopener');
}

/** Posluchač v hlavním okně: zpráva z okna mapy → navigace aplikace. */
export function useNavigaceZOkna(): void {
  const navigate = useNavigate();
  useEffect(() => {
    const naZpravu = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const cesta = cestaZeZpravy(e.data);
      if (!cesta) return;
      void navigate(cesta);
    };
    window.addEventListener('message', naZpravu);
    return () => window.removeEventListener('message', naZpravu);
  }, [navigate]);
}

/** Komponenta pro `App.tsx` (hook uvnitř routeru). */
export function NavigaceZOkna(): null {
  useNavigaceZOkna();
  return null;
}
